import React from "react";
import clsx from "clsx";

import { Button } from "V2.0/common/button";
import { FocusState } from "../models/guidedFocus";

type Props = {
  id: string;
  number: number | null;
  title: string;
  summary: string;
  state: FocusState;
  nextLabel?: string | null;
  onNext?: () => void;
  onReveal?: () => void;
  children: React.ReactNode;
};

export function SectionCard({ id, number, title, summary, state, nextLabel, onNext, onReveal, children }: Props) {
  const locked = state === "locked";
  const heading = number ? `${number}. ${title}` : title;
  return (
    <section
      data-testid={`section-${id}`}
      data-state={state}
      aria-label={heading}
      onClick={locked ? onReveal : undefined}
      className={clsx(
        "rounded-lg border bg-white",
        state === "active" ? "border-blue-300 ring-2 ring-blue-500/20" : "border-zinc-200",
        locked && "cursor-pointer"
      )}
    >
      <header className="flex items-start justify-between gap-4 border-b border-zinc-100 px-5 py-4">
        <div>
          <h3 className="text-base font-semibold text-zinc-900">{heading}</h3>
          <p className="mt-1 text-sm text-zinc-500">{summary}</p>
        </div>
        {locked && <span className="shrink-0 text-xs font-medium text-zinc-400">Coming up</span>}
      </header>
      <fieldset
        disabled={locked}
        aria-hidden={locked || undefined}
        className={clsx("flex flex-col gap-6 px-5 py-5", locked && "pointer-events-none select-none opacity-50 blur-[1.5px]")}
      >
        {children}
      </fieldset>
      {state === "active" && nextLabel && (
        <footer className="flex justify-end border-t border-zinc-100 px-5 py-3">
          <Button onClick={onNext}>{nextLabel}</Button>
        </footer>
      )}
    </section>
  );
}
